import { useState, useEffect, useMemo, useRef } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Card } from './ui/card';
import { Badge } from './ui/badge';
import {
    Camera, FlaskConical, MessageCircle, Sparkles, Zap,
    CheckCircle2, Flame, Apple, Pause, Play,
} from 'lucide-react';

/**
 * Interactive 3-step landing demo. No video — pure CSS/React mockups.
 * Auto-rotates every 5s, pauses on hover, manual dots for direct navigation.
 * Designed to give visitors the "aha" moment BEFORE registering.
 */
export function LiveDemoCarousel({ isDe }) {
    const [activeIdx, setActiveIdx] = useState(0);
    const [paused, setPaused] = useState(false);
    const timerRef = useRef(null);

    const STEPS = useMemo(() => (isDe ? [
        {
            id: 'food-scan',
            chip: '📸 Schritt 1',
            title: 'Foto vom Teller → Nährwerte in 2 Sek.',
            description: 'Egal ob Restaurant, Mensa oder selbstgemacht — die KI erkennt Gericht und Portion.',
            mockup: 'food',
            accentClass: 'from-emerald-500/20 to-lime-500/10 border-emerald-500/30',
        },
        {
            id: 'lab-upload',
            chip: '🩸 Schritt 2',
            title: 'Befund hochladen → automatische Quest',
            description: 'Nanouk liest 36 Marker aus deinem Blutbild und macht aus Defiziten konkrete Mini-Challenges.',
            mockup: 'lab',
            accentClass: 'from-violet-500/20 to-indigo-500/10 border-violet-500/30',
        },
        {
            id: 'coach-reply',
            chip: '🐺 Schritt 3',
            title: 'Frag Nanouk — sie weiß, was los ist',
            description: 'Statt generischer Tipps zitiert sie deine Werte: „Dein Eisen ist niedrig, dazu gestern wenig Schlaf — heute eine kleine Portion Linsen?"',
            mockup: 'coach',
            accentClass: 'from-amber-500/20 to-orange-500/10 border-amber-500/30',
        },
    ] : [
        {
            id: 'food-scan',
            chip: '📸 Step 1',
            title: 'Snap your plate → nutrients in 2 seconds',
            description: 'Restaurant, cafeteria, homemade — the AI recognizes dish and portion.',
            mockup: 'food',
            accentClass: 'from-emerald-500/20 to-lime-500/10 border-emerald-500/30',
        },
        {
            id: 'lab-upload',
            chip: '🩸 Step 2',
            title: 'Upload labs → automatic quest',
            description: 'Nanouk reads 36 markers from your bloodwork and turns deficits into concrete mini-challenges.',
            mockup: 'lab',
            accentClass: 'from-violet-500/20 to-indigo-500/10 border-violet-500/30',
        },
        {
            id: 'coach-reply',
            chip: '🐺 Step 3',
            title: 'Ask Nanouk — she knows what\'s going on',
            description: 'Instead of generic tips she cites your data: "Your iron is low, plus you slept little yesterday — how about a small lentil portion today?"',
            mockup: 'coach',
            accentClass: 'from-amber-500/20 to-orange-500/10 border-amber-500/30',
        },
    ]), [isDe]);

    useEffect(() => {
        if (paused) return undefined;
        timerRef.current = setTimeout(() => {
            setActiveIdx((i) => (i + 1) % STEPS.length);
        }, 5500);
        return () => clearTimeout(timerRef.current);
    }, [activeIdx, paused, STEPS.length]);

    const active = STEPS[activeIdx];

    return (
        <div
            className="max-w-3xl mx-auto"
            data-testid="live-demo-carousel"
            onMouseEnter={() => setPaused(true)}
            onMouseLeave={() => setPaused(false)}
        >
            <Card className={`overflow-hidden border bg-gradient-to-br ${active.accentClass}`}>
                <div className="grid md:grid-cols-2 gap-0">
                    {/* Left: copy */}
                    <div className="p-6 md:p-8 flex flex-col justify-center space-y-4 order-2 md:order-1">
                        <div className="flex items-center gap-2">
                            <Badge variant="secondary" className="text-xs font-medium" data-testid={`demo-step-chip-${activeIdx}`}>
                                {active.chip}
                            </Badge>
                            <button
                                type="button"
                                onClick={() => setPaused(p => !p)}
                                className="ml-auto text-muted-foreground hover:text-foreground transition-colors"
                                aria-label={paused ? 'Play' : 'Pause'}
                                data-testid="demo-play-pause"
                            >
                                {paused ? <Play className="w-3.5 h-3.5" /> : <Pause className="w-3.5 h-3.5" />}
                            </button>
                        </div>
                        <h3 className="font-display text-xl md:text-2xl font-bold leading-tight">
                            {active.title}
                        </h3>
                        <p className="text-sm md:text-base text-muted-foreground leading-relaxed">
                            {active.description}
                        </p>

                        {/* Dots / step navigation */}
                        <div className="flex items-center gap-2 pt-1" role="tablist" aria-label="Demo steps">
                            {STEPS.map((s, i) => (
                                <button
                                    key={s.id}
                                    role="tab"
                                    aria-selected={i === activeIdx}
                                    type="button"
                                    onClick={() => setActiveIdx(i)}
                                    className={`h-1.5 rounded-full transition-all duration-300 ${
                                        i === activeIdx
                                            ? 'w-8 bg-foreground'
                                            : 'w-1.5 bg-muted-foreground/40 hover:bg-muted-foreground/70'
                                    }`}
                                    data-testid={`demo-dot-${i}`}
                                    aria-label={`Go to step ${i + 1}`}
                                />
                            ))}
                        </div>
                    </div>

                    {/* Right: mockup */}
                    <div className="relative aspect-[4/3] md:aspect-auto md:min-h-[340px] bg-card/40 backdrop-blur-sm overflow-hidden order-1 md:order-2">
                        <AnimatePresence mode="wait">
                            <motion.div
                                key={active.id}
                                initial={{ opacity: 0, y: 20 }}
                                animate={{ opacity: 1, y: 0 }}
                                exit={{ opacity: 0, y: -20 }}
                                transition={{ duration: 0.35 }}
                                className="absolute inset-0 p-4 md:p-6 flex items-center justify-center"
                            >
                                {active.mockup === 'food' && <FoodMockup isDe={isDe} />}
                                {active.mockup === 'lab' && <LabMockup isDe={isDe} />}
                                {active.mockup === 'coach' && <CoachMockup isDe={isDe} />}
                            </motion.div>
                        </AnimatePresence>
                    </div>
                </div>
            </Card>
        </div>
    );
}

function FoodMockup({ isDe }) {
    return (
        <div className="w-full max-w-xs bg-card border rounded-2xl shadow-lg p-3 space-y-2" data-testid="demo-mockup-food">
            {/* Photo placeholder */}
            <div className="aspect-[4/3] rounded-xl bg-gradient-to-br from-amber-200 via-orange-300 to-red-300 dark:from-amber-700 dark:via-orange-800 dark:to-red-900 flex items-center justify-center relative overflow-hidden">
                <Camera className="w-10 h-10 text-white/90" />
                <Badge className="absolute top-2 right-2 bg-emerald-500 text-white border-0 text-[10px] gap-1">
                    <CheckCircle2 className="w-3 h-3" />
                    {isDe ? 'Erkannt' : 'Detected'}
                </Badge>
            </div>
            {/* Detected info */}
            <div className="space-y-1.5 px-1">
                <p className="font-semibold text-sm flex items-center gap-1.5">
                    <Apple className="w-3.5 h-3.5 text-emerald-500" />
                    {isDe ? 'Avocado-Toast' : 'Avocado toast'}
                </p>
                <div className="grid grid-cols-3 gap-1 text-[10px]">
                    <div className="bg-muted/50 rounded p-1.5 text-center">
                        <div className="font-bold text-foreground">312</div>
                        <div className="text-muted-foreground">kcal</div>
                    </div>
                    <div className="bg-muted/50 rounded p-1.5 text-center">
                        <div className="font-bold text-foreground">14g</div>
                        <div className="text-muted-foreground">protein</div>
                    </div>
                    <div className="bg-muted/50 rounded p-1.5 text-center">
                        <div className="font-bold text-foreground">22g</div>
                        <div className="text-muted-foreground">{isDe ? 'Fett' : 'fat'}</div>
                    </div>
                </div>
            </div>
        </div>
    );
}

function LabMockup({ isDe }) {
    return (
        <div className="w-full max-w-xs space-y-2" data-testid="demo-mockup-lab">
            {/* Top: extracted markers */}
            <div className="bg-card border rounded-2xl shadow-lg p-3 space-y-2">
                <div className="flex items-center gap-1.5">
                    <FlaskConical className="w-4 h-4 text-violet-500" />
                    <p className="text-xs font-semibold">{isDe ? '36 Marker erkannt' : '36 markers detected'}</p>
                </div>
                <div className="space-y-1">
                    <MarkerRow name="Vitamin D" value="18 ng/ml" status="low" labelLow={isDe ? 'niedrig' : 'low'} labelNormal={isDe ? 'normal' : 'normal'} />
                    <MarkerRow name="Ferritin" value="42 ng/ml" status="normal" labelLow={isDe ? 'niedrig' : 'low'} labelNormal={isDe ? 'normal' : 'normal'} />
                    <MarkerRow name="HDL" value="58 mg/dl" status="normal" labelLow={isDe ? 'niedrig' : 'low'} labelNormal={isDe ? 'normal' : 'normal'} />
                </div>
            </div>
            {/* Auto-quest banner */}
            <motion.div
                initial={{ opacity: 0, x: -10 }}
                animate={{ opacity: 1, x: 0 }}
                transition={{ delay: 0.4 }}
                className="bg-gradient-to-r from-violet-500/20 to-indigo-500/20 border border-violet-500/40 rounded-xl p-2.5 flex items-center gap-2"
            >
                <Sparkles className="w-4 h-4 text-violet-600 dark:text-violet-400 flex-shrink-0" />
                <div className="flex-1 min-w-0">
                    <p className="text-[11px] font-bold leading-tight">
                        🌞 {isDe ? 'Sonnen-Quest aktiv' : 'Sun-Quest active'}
                    </p>
                    <p className="text-[10px] text-muted-foreground leading-tight">
                        {isDe ? '3 × 20 Min Tageslicht · +120 XP' : '3 × 20 min daylight · +120 XP'}
                    </p>
                </div>
                <Zap className="w-3.5 h-3.5 text-amber-500" />
            </motion.div>
        </div>
    );
}

function MarkerRow({ name, value, status, labelLow, labelNormal }) {
    const isLow = status === 'low';
    return (
        <div className="flex items-center justify-between gap-2 text-[11px]">
            <span className="text-muted-foreground truncate flex-1">{name}</span>
            <span className="font-mono text-foreground">{value}</span>
            <Badge
                variant={isLow ? 'destructive' : 'secondary'}
                className={`text-[9px] px-1.5 py-0 ${isLow ? '' : 'bg-emerald-500/20 text-emerald-700 dark:text-emerald-400 border-emerald-500/30'}`}
            >
                {isLow ? labelLow : labelNormal}
            </Badge>
        </div>
    );
}

function CoachMockup({ isDe }) {
    return (
        <div className="w-full max-w-xs space-y-2" data-testid="demo-mockup-coach">
            {/* User question */}
            <div className="flex justify-end">
                <div className="bg-primary text-primary-foreground rounded-2xl rounded-tr-sm px-3 py-2 text-xs max-w-[80%]">
                    {isDe ? 'Wie geht\'s mir heute?' : 'How am I doing today?'}
                </div>
            </div>
            {/* Nanouk reply */}
            <motion.div
                initial={{ opacity: 0, y: 8 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.3 }}
                className="flex items-start gap-2"
            >
                <div className="w-7 h-7 rounded-full bg-gradient-to-br from-amber-400 to-orange-500 flex items-center justify-center text-sm flex-shrink-0">
                    🐺
                </div>
                <div className="bg-card border rounded-2xl rounded-tl-sm px-3 py-2 text-xs space-y-1.5 max-w-[85%]">
                    <p className="leading-relaxed">
                        {isDe ? (
                            <>Dein <strong>Eisen ist niedrig</strong> (42) und du hast gestern nur <strong>5,5h geschlafen</strong>. Klein anfangen:</>
                        ) : (
                            <>Your <strong>iron is low</strong> (42) and you slept just <strong>5.5h</strong> last night. Start small:</>
                        )}
                    </p>
                    <div className="flex items-center gap-1.5 text-[10px] bg-emerald-500/10 border border-emerald-500/30 rounded-lg px-2 py-1">
                        <Flame className="w-3 h-3 text-orange-500" />
                        {isDe ? 'Mittag: Linsensuppe (+ Eisen)' : 'Lunch: lentil soup (+ iron)'}
                    </div>
                    <div className="flex items-center gap-1.5 text-[10px] bg-violet-500/10 border border-violet-500/30 rounded-lg px-2 py-1">
                        <MessageCircle className="w-3 h-3 text-violet-600 dark:text-violet-400" />
                        {isDe ? 'Heute Abend: 30 Min früher ins Bett' : 'Tonight: 30 min earlier to bed'}
                    </div>
                </div>
            </motion.div>
        </div>
    );
}
